import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cn } from '@/lib/utils/classNames';
import { GgcButton } from './button';

/**
 * GGC V2. Dialog — modal/dialog built on Radix Dialog primitives.
 *
 * The Figma file does not include a dialog spec, so the styling follows the
 * design system language: rounded-ggc-lg, shadow-ggc-lg, 24px padding,
 * white surface, dimmed backdrop.
 *
 * Composition mirrors Radix UI / shadcn:
 *   <GgcDialog>
 *     <GgcDialogTrigger>...</GgcDialogTrigger>
 *     <GgcDialogContent>
 *       <GgcDialogHeader>
 *         <GgcDialogTitle>...</GgcDialogTitle>
 *         <GgcDialogDescription>...</GgcDialogDescription>
 *       </GgcDialogHeader>
 *       ...body...
 *       <GgcDialogFooter>...buttons...</GgcDialogFooter>
 *     </GgcDialogContent>
 *   </GgcDialog>
 */
const GgcDialog = DialogPrimitive.Root;
const GgcDialogTrigger = DialogPrimitive.Trigger;
const GgcDialogPortal = DialogPrimitive.Portal;
const GgcDialogClose = DialogPrimitive.Close;

const GgcDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // z-[9000]: Tailwind's default z-50 sits below several fixed-position
      // panels in this app (e.g. the dashboard channel-details drawer at
      // z-index:1051), which would render underneath the dialog. Pick a value
      // above all drawers/panels but below the toast layer (z-[9998] in
      // Snackbar.tsx) and the full-page spinner overlay (z-index:70000), so a
      // dialog always sits above page content but a toast fired from a
      // dialog action stays visible on top of it.
      'fixed inset-0 z-[9000] bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
));
GgcDialogOverlay.displayName = 'GgcDialogOverlay';

interface GgcDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Hide the default close (X) button. */
  hideCloseButton?: boolean;
  /** aria-label for the built-in close button. Default `"Close"`. */
  closeLabel?: string;
}

const GgcDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  GgcDialogContentProps
>(({ className, children, hideCloseButton, closeLabel, ...props }, ref) => (
  <GgcDialogPortal>
    <GgcDialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        // z-[9000]: must match GgcDialogOverlay above (both need to clear
        // fixed-position drawers/panels like the dashboard channel-details
        // drawer at z-index:1051 — see the Overlay's comment for the full scale).
        'fixed left-1/2 top-1/2 z-[9000] grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-ggc-lg border border-ggc-border-gray bg-white p-6 font-ggc shadow-ggc-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        className,
      )}
      {...props}
    >
      {children}
      {!hideCloseButton && (
        <DialogPrimitive.Close
          aria-label={closeLabel ?? 'Close'}
          className="absolute right-4 top-4 inline-flex size-6 items-center justify-center rounded-ggc-sm text-ggc-text-gray transition-colors hover:bg-ggc-bg-base hover:text-ggc-text-primary focus:outline-none focus:ring-2 focus:ring-ggc-cta-blue/40"
        >
          ✕
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </GgcDialogPortal>
));
GgcDialogContent.displayName = 'GgcDialogContent';

function GgcDialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('flex flex-col gap-1', className)} {...props} />;
}

function GgcDialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

const GgcDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('ggc-text-page-title text-ggc-text-primary', className)}
    {...props}
  />
));
GgcDialogTitle.displayName = 'GgcDialogTitle';

const GgcDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('ggc-text-body text-ggc-text-gray', className)}
    {...props}
  />
));
GgcDialogDescription.displayName = 'GgcDialogDescription';

/**
 * Convenience component for the most common dialog shape: title + body +
 * confirm/cancel buttons. Tone changes the confirm button color.
 */
export interface GgcConfirmDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: React.ReactNode;
  cancelLabel?: React.ReactNode;
  tone?: 'primary' | 'danger';
  onConfirm?: () => void | Promise<void>;
  loading?: boolean;
  children?: React.ReactNode;
}

function GgcConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  onConfirm,
  loading,
  children,
}: GgcConfirmDialogProps) {
  return (
    <GgcDialog open={open} onOpenChange={onOpenChange}>
      <GgcDialogContent>
        <GgcDialogHeader>
          <GgcDialogTitle>{title}</GgcDialogTitle>
          {description && <GgcDialogDescription>{description}</GgcDialogDescription>}
        </GgcDialogHeader>
        {children}
        <GgcDialogFooter>
          <GgcDialogClose asChild>
            <GgcButton variant="secondary" size="default">
              {cancelLabel}
            </GgcButton>
          </GgcDialogClose>
          <GgcButton
            variant={tone === 'danger' ? 'danger' : 'primary'}
            size="default"
            onClick={onConfirm}
            disabled={loading}
          >
            {confirmLabel}
          </GgcButton>
        </GgcDialogFooter>
      </GgcDialogContent>
    </GgcDialog>
  );
}

export {
  GgcDialog,
  GgcDialogTrigger,
  GgcDialogClose,
  GgcDialogContent,
  GgcDialogHeader,
  GgcDialogFooter,
  GgcDialogTitle,
  GgcDialogDescription,
  GgcDialogOverlay,
  GgcConfirmDialog,
};
